import BillingsOrderForwardTaxiTaxiAddParams, {
  BillingsOrderForwardTaxiTaxiAddParamsBillingNode,
  BillingsOrderForwardTaxiTaxiAddParamsBonusContribution,
  BillingsOrderForwardTaxiTaxiAddParamsContribution,
  BillingsOrderForwardTaxiTaxiAddParamsContributionType,
  BillingsOrderForwardTaxiTaxiAddParamsPenaltyContribution,
} from "../../../../../common/services/billings/order-forward/taxi-taxi/add/billings-order-forward-taxi-taxi-add-params";
import BillingsNode from "../../../common/types/billings-node";
import BillingsTaxiTaxiContributionBonus, {
  BillingsTaxiTaxiContributionBonusType,
} from "../../common/contributions/bonus/types/billings-taxi-taxi-contributions-bonus";
import BillingsTaxiTaxiContributionPenalty, {
  BillingsTaxiTaxiContributionPenaltyType,
} from "../../common/contributions/penalty/types/billings-taxi-taxi-contributions-penalty";
import BillingFormData, {
  BillingFormDataContributions,
} from "../types/billing-form.data";

const createBillingBonusContribution = (
  contribution: BillingsTaxiTaxiContributionBonus,
): BillingsOrderForwardTaxiTaxiAddParamsBonusContribution | undefined => {
  switch (contribution.type) {
    case BillingsTaxiTaxiContributionBonusType.BONUS_FAVORABLE_DISTANCE:
      return {
        type: BillingsOrderForwardTaxiTaxiAddParamsContributionType.BONUS_FAVORABLE_DISTANCE,
        comment: contribution.comment ?? "",
        distance: contribution.distance ?? 0,
        rate: contribution.rate ?? 0,
      };
    case BillingsTaxiTaxiContributionBonusType.BONUS_OTHER:
      return {
        type: BillingsOrderForwardTaxiTaxiAddParamsContributionType.BONUS_OTHER,
        comment: contribution.comment ?? "",
        amount: contribution.amount ?? 0,
      };
    default:
      return undefined;
  }
};

const createBillingPenaltyContribution = (
  contribution: BillingsTaxiTaxiContributionPenalty,
): BillingsOrderForwardTaxiTaxiAddParamsPenaltyContribution | undefined => {
  switch (contribution.type) {
    case BillingsTaxiTaxiContributionPenaltyType.PENALTY_BEING_LATE:
      return {
        type: BillingsOrderForwardTaxiTaxiAddParamsContributionType.PENALTY_BEING_LATE,
        comment: contribution.comment ?? "",
        amount: contribution.amount ?? 0,
      };
    case BillingsTaxiTaxiContributionPenaltyType.PENALTY_INCOMPATIBLE_CAR:
      return {
        type: BillingsOrderForwardTaxiTaxiAddParamsContributionType.PENALTY_INCOMPATIBLE_CAR,
        comment: contribution.comment ?? "",
        amount: contribution.amount ?? 0,
      };
    case BillingsTaxiTaxiContributionPenaltyType.PENALTY_OTHER:
      return {
        type: BillingsOrderForwardTaxiTaxiAddParamsContributionType.PENALTY_OTHER,
        comment: contribution.comment ?? "",
        amount: contribution.amount ?? 0,
      };
    case BillingsTaxiTaxiContributionPenaltyType.PENALTY_WRONG_ROUTE:
      return {
        type: BillingsOrderForwardTaxiTaxiAddParamsContributionType.PENALTY_WRONG_ROUTE,
        comment: contribution.comment ?? "",
        amount: contribution.amount ?? 0,
      };
  }
};

const createBillingContributions = (
  contributions: BillingFormDataContributions,
): BillingsOrderForwardTaxiTaxiAddParamsContribution[] => {
  const bonusContributions: BillingsOrderForwardTaxiTaxiAddParamsBonusContribution[] =
    contributions.bonus.map(
      (contribution) => createBillingBonusContribution(contribution)!,
    );

  const penaltyContributions: BillingsOrderForwardTaxiTaxiAddParamsPenaltyContribution[] =
    contributions.penalty.map(
      (contribution) => createBillingPenaltyContribution(contribution)!,
    );

  return [...bonusContributions, ...penaltyContributions];
};

const createSaveBillingParamsBillingNode = (
  node: BillingsNode,
): BillingsOrderForwardTaxiTaxiAddParamsBillingNode => {
  return {
    allowCharge: node.isHighwayAllowed,
    checkoutDate: node.checkoutDate,
    checkoutEventId: node.checkoutEventUuid,
    description: node.description,
    distance: node.distance,
    editableCoordinates: node.isEditable,
    haltingTime: node.haltingTime,
    highwayCharge: node.highwayCharge,
    id: node.id,
    lat: node.lat,
    lon: node.lon,
    nodeId: node.nodeId,
    orderId: node.orderId,
    plannedDate: node.plannedDate,
    plannedDistance: node.plannedDistance,
  };
};

const createSaveBillingParamsBillingNodes = (
  nodes: BillingsNode[],
): BillingsOrderForwardTaxiTaxiAddParamsBillingNode[] => {
  return nodes.map(createSaveBillingParamsBillingNode);
};

const createSaveBillingParams = (
  data: BillingFormData,
  forwardingId: string,
): BillingsOrderForwardTaxiTaxiAddParams => {
  return {
    forwardingId,
    billingNodes: createSaveBillingParamsBillingNodes(data.billingNodes),
    billingContributions: createBillingContributions(data.billingContributions),
  };
};

const billingSaveRequestFactory = {
  createSaveBillingParams,
};

export default billingSaveRequestFactory;
